// tslint:disable:trailing-comma

import { hybridFetch } from '../bridge';
import { stat, PrefetchDataTunnelBids } from '../stat';
import { env } from '../context/env';
import neoParams from '../context/params';
import { NeoNamespace } from '../common-enum';
import { NeoError } from '../util/neo-error';
import { debug } from '../util/debug';

export interface GetPrefetchDataOptions {
  ajaxFunc?: () => Promise<any>;
  ajaxDelay?: number;
}

export enum PrefetchSource {
  Bridge = 'bridge',
  Ajax = 'ajax',
}

export class PrefetchDataTunnel {
  private static defaultGetPrefetchDataOptions: GetPrefetchDataOptions = {
    ajaxDelay: 2500,
  };

  // 预请求数据 Map
  // 可支持多个预请求数据，key 是预请求数据 key，value 是请求数据 Promise
  private prefetchDataMap: Map<string, Promise<any>> = new Map();

  // 预请求数据有效标识 Map
  // 通过桥调用获取到的预请求数据是单次有效的，首次调用 getPrefetchData() 方法后会将对应的有效标识置为 false
  private prefetchDataValidMap: Map<string, boolean> = new Map();

  // 获取数据 Promise
  private fetchPromise?: Promise<any>;

  /**
   * 获取数据，调用获取主接口数据桥 (neohybrid.nsf)，完成后更新预请求数据 Promise 状态
   * @param {string[]} prefetchKeyList 预请求数据 key 数组
   * @returns {Promise} 获取数据 Promise
   */
  fetch(prefetchKeyList: string[]): Promise<any> {
    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    if (!prefetchKeyList.length) {
      this.fetchPromise = Promise.resolve();
      return this.fetchPromise;
    }

    const { isPageReload } = env.getEnv();
    const { enable_data_loader: enableDataLoader } = neoParams.getParams();
    const fetchDataPromiseList: Array<Promise<any>> = [];

    prefetchKeyList.forEach((key: string) => {
      if (this.prefetchDataMap.has(key)) {
        return;
      }

      stat.sendToLX(PrefetchDataTunnelBids.FetchStart, { key });
      stat.sendMetric('neo_pre_request_start', 1, { key });

      const requestStartTime = new Date().getTime();
      let requestTime: number;
      // 创建预请求数据 Promise
      const prefetchDataPromise = new Promise<any>((resolve, reject) => {
        // 页面重新加载，不会调用获取主接口数据桥
        if (isPageReload) {
          debug(NeoNamespace.CorePrefetchDataTunnel, 2, key);
          stat.sendToLX(PrefetchDataTunnelBids.FetchFail, {
            key,
            bridge_time: 0,
            error_message: 'page reload',
          });
          stat.sendMetric('neo_pre_request_fail', 1, {
            key,
            error_message: 'page reload',
          });
          reject(
            new NeoError(
              NeoNamespace.CorePrefetchDataTunnel,
              'can not load prefetch data on reloaded page'
            )
          );
          return;
        }

        // 未开启 shark 能力，不会调用获取主接口数据桥
        if (enableDataLoader !== '1') {
          debug(
            NeoNamespace.CorePrefetchDataTunnel,
            2,
            `params.enable_data_loader: ${enableDataLoader}`
          );
          stat.sendToLX(PrefetchDataTunnelBids.FetchFail, {
            key,
            bridge_time: 0,
            error_message: 'shark disabled',
          });
          stat.sendMetric('neo_pre_request_fail', 1, {
            key,
            error_message: 'shark disabled',
          });
          reject(
            new NeoError(
              NeoNamespace.CorePrefetchDataTunnel,
              `can not load prefetch data when params.enable_data_loader = ${enableDataLoader}`
            )
          );
          return;
        }

        // 发起桥调用
        hybridFetch({ data: { key } })
          .finally(() => {
            requestTime = new Date().getTime() - requestStartTime;
          })
          .then((data: any): void => {
            stat.sendToLX(PrefetchDataTunnelBids.FetchSuccess, {
              key,
              bridge_time: requestTime,
            });
            stat.sendMetric('neo_pre_request_succ', requestTime, { key });

            resolve(data);
          })
          .catch((err?: any): void => {
            stat.sendToLX(PrefetchDataTunnelBids.FetchFail, {
              key,
              bridge_time: requestTime,
              error_message: 'shark disabled',
            });
            stat.sendMetric('neo_pre_request_fail', requestTime, {
              key,
              error_message: err?.message,
            });
            reject(err);
          });
      });

      // 相关集合属性中添加预请求数据对象
      this.prefetchDataMap.set(key, prefetchDataPromise);
      this.prefetchDataValidMap.set(key, true);
      fetchDataPromiseList.push(prefetchDataPromise);
    });

    // TODO 替换为 Promise.allSettled
    this.fetchPromise = Promise.all(fetchDataPromiseList).catch((err) => {
      debug(NeoNamespace.CorePrefetchDataTunnel, 1, err);
    });

    return this.fetchPromise;
  }

  /**
   * 获取预请求数据
   * @param {string} key 预请求数据 key，存在多个预请求数据的场景下，通过 key 值区分
   * @param {GetPrefetchDataOptions} options 请求选项
   * @returns {Promise} 预请求数据 Promise
   */
  getPrefetchData(
    key: string,
    options: GetPrefetchDataOptions = {}
  ): Promise<any> {
    const prefetchDataPromise = this.prefetchDataMap.get(key);
    const prefetchDataValid = this.prefetchDataValidMap.get(key);
    const { enable_data_loader: enableDataLoader } = neoParams.getParams();
    const mergedOptions = {
      ...PrefetchDataTunnel.defaultGetPrefetchDataOptions,
      ...options,
    };
    const { ajaxFunc, ajaxDelay } = mergedOptions;

    stat.sendToLX(PrefetchDataTunnelBids.GetDataStart, { key });
    stat.sendMetric('get_prefetch_data_start', 1, { key });

    if (!prefetchDataPromise) {
      stat.sendToLX(PrefetchDataTunnelBids.GetDataFail, {
        key,
        request_time: 0,
        error_message: 'unknown prefetch key',
      });
      stat.sendMetric('get_prefetch_data_fail', 1, {
        key,
        error_message: 'unknown prefetch key',
      });
      return Promise.reject(
        new NeoError(
          NeoNamespace.CorePrefetchDataTunnel,
          `unknown prefetch key: ${key}`
        )
      );
    }

    // 非首次调用（有效标识为 false）或者未开启 shark 能力，立即通过 AJAX 获取数据
    if (!prefetchDataValid || enableDataLoader !== '1') {
      if (typeof ajaxFunc === 'function') {
        return this.getPrefetchDataByAjax(key, ajaxFunc);
      }
      stat.sendToLX(PrefetchDataTunnelBids.GetDataFail, {
        key,
        request_time: 1,
        error_message: 'require ajax function',
      });
      stat.sendMetric('get_prefetch_data_fail', 1, {
        key,
        error_message: 'require ajax function',
      });
      return Promise.reject(
        new NeoError(
          NeoNamespace.CorePrefetchDataTunnel,
          `getPrefetchData with ${key} require options.ajaxFunc`
        )
      );
    }

    // 首次调用（有效标识为 true）
    this.prefetchDataValidMap.set(key, false);
    const promise = new Promise((resolve, reject) => {
      // 延迟执行 AJAX 计时器，通过 reject 表示延迟完成
      const delay = new Promise((resolveDelay, rejectDelay) => {
        setTimeout(rejectDelay, ajaxDelay);
      });
      // 数据获取完成标识
      let isSettled = false;

      // 桥调用成功，返回预请求数据，不一定是有效的 resolve（可能在 AJAX 调用成功之后）
      prefetchDataPromise
        .then((data) => {
          isSettled = true;
          resolve(this.wrapData(data, { fromShark: true }));
        })
        .catch((err) => {
          debug(NeoNamespace.CorePrefetchDataTunnel, 1, err);
        });

      // 桥调用失败或者延迟执行 AJAX 计时器完成，调用 AJAX 获取数据
      Promise.race([prefetchDataPromise, delay]).catch((err) => {
        // 如果数据获取已完成（桥调用成功），则不执行后续逻辑
        if (isSettled) {
          return;
        }

        if (typeof ajaxFunc === 'function') {
          this.getPrefetchDataByAjax(key, ajaxFunc)
            .then((data) => {
              resolve(this.wrapData(data, { fromShark: false }));
            })
            .catch((ajaxErr?: any) => {
              reject(ajaxErr);
            });
        } else {
          reject(err);
        }
      });
    });
    const requestStartTime = new Date().getTime();
    let requestTime: number;

    promise
      .finally(() => {
        requestTime = new Date().getTime() - requestStartTime;
      })
      .then((data: any) => {
        stat.sendToLX(PrefetchDataTunnelBids.GetDataSuccess, {
          key,
          request_time: requestTime,
          type: data?.__neoFromShark__ ? 'shark' : 'ajax',
        });
        stat.sendMetric('get_prefetch_data_succ', requestTime, {
          key,
          type: data?.__neoFromShark__ ? 'shark' : 'ajax',
        });
      })
      .catch((err: any) => {
        stat.sendToLX(PrefetchDataTunnelBids.GetDataFail, {
          key,
          request_time: requestTime,
          error_message: err && err.toString(),
        });
        stat.sendMetric('get_prefetch_data_fail', requestTime, {
          key,
          error_message: err && err.toString(),
        });
      });

    return promise;
  }

  private getPrefetchDataByAjax(
    key: string,
    ajaxFunc: () => Promise<any>
  ): Promise<any> {
    stat.sendToLX(PrefetchDataTunnelBids.GetDataAjaxStart, { key });
    stat.sendMetric('get_prefetch_data_ajax_start', 1, { key });
    return ajaxFunc.call(null);
  }

  private wrapData(data: any, params: any) {
    const { fromShark } = params;
    if (data !== null && typeof data === 'object') {
      try {
        Object.assign(data, { __neoFromShark__: fromShark });
      } catch (err) {
        console.log(err);
      }
    }

    return data;
  }
}

const prefetchDataTunnel = new PrefetchDataTunnel();

export { prefetchDataTunnel };
export default prefetchDataTunnel;
